import { Avatar, Badge, Box, Flex, Heading, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, SimpleGrid, Spinner, Text, } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { COLORS, UsersIcon } from "../Token";
import { TeamDetails } from "../Types";
import { inductionService } from "../services/inductionService";

interface TeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamLeadId?: number;
  teamLeadName?: string;
  departmentName?: string;
  departmentId?: number;
}

export function TeamModal({
  isOpen,
  onClose,
  teamLeadId,
  teamLeadName,
  departmentName,
  departmentId,
}: TeamModalProps) {
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState<TeamDetails | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    inductionService
      .getTeamMembers(teamLeadId, teamLeadName, departmentId)
      .then((res) => {
        setDetails(res);
      })
      .catch(() => {
        setDetails(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isOpen, teamLeadId, teamLeadName, departmentId]);

  const teamLead = details?.members.find((m) => m.isTeamLead);
  const otherMembers = details?.members.filter((m) => !m.isTeamLead) || [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="20px" bg="white" p={2}>
        <ModalHeader pb={2}>
          <Flex align="center" gap={3}>
            <Flex w="40px" h="40px" borderRadius="12px" bg="#EAF5EE" color={COLORS.brand} align="center" justify="center">
              <UsersIcon boxSize={5} />
            </Flex>
            <Box>
              <Heading size="md" color={COLORS.ink}>{details?.teamName || `Team ${teamLeadName || "Members"}`}</Heading>
              <Text fontSize="sm" color={COLORS.inkSoft}>{departmentName || "Department"} · {details?.memberCount || otherMembers.length + (teamLead ? 1 : 0)} team members</Text>
            </Box>
          </Flex>
        </ModalHeader>
        <ModalCloseButton top={4} right={4} />

        <ModalBody py={4}>
          {loading ? (
            <Flex justify="center" align="center" py={12}>
              <Spinner size="lg" color={COLORS.brand} />
            </Flex>
          ) : (
            <Flex direction="column" gap={5}>
              {/* Featured Team Lead Card */}
              {teamLead && (
                <Box p={4} borderRadius="16px" bg="#F0F8F3" border="1.5px solid #CFE5D8">
                  <Flex align="center" justify="space-between" mb={2}>
                    <Badge colorScheme="green" px={2.5} py={0.5} borderRadius="full">Team Lead</Badge>
                    <Text fontSize="xs" color={COLORS.inkSoft}>Reporting to: <b>{teamLead.reportingManager}</b></Text>
                  </Flex>
                  <Flex align="center" gap={4}>
                    <Avatar size="md" name={teamLead.name} src={teamLead.profileImage} bg={COLORS.brand} color="white" />
                    <Box>
                      <Text fontWeight={700} fontSize="17px" color={COLORS.ink}>{teamLead.name}</Text>
                      <Text fontSize="sm" color={COLORS.brand} fontWeight={500}>{teamLead.designation}</Text>
                      <Text fontSize="xs" color={COLORS.inkSoft} mt={0.5}>Experience: {teamLead.experience}</Text>
                    </Box>
                  </Flex>
                </Box>
              )}

              {/* Team Members Grid */}
              <Box>
                <Text fontSize="sm" fontWeight={600} color={COLORS.inkSoft} mb={3}>Team Members</Text>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                  {otherMembers.map((member) => (
                    <Box key={member.employeeId} p={3.5} borderRadius="14px" border="1px solid" borderColor={COLORS.line} bg="#FBFDFC" _hover={{ borderColor: COLORS.brand, boxShadow: "sm" }} transition="all 0.2s">
                      <Flex align="center" gap={3}>
                        <Avatar size="sm" name={member.name} src={member.profileImage} bg="#DCE9E1" color={COLORS.brand} />
                        <Box minW={0} flexGrow={1}>
                          <Text fontWeight={600} fontSize="15px" color={COLORS.ink} isTruncated>{member.name}</Text>
                          <Text fontSize="xs" color={COLORS.brand} isTruncated>{member.designation}</Text>
                          <Flex justify="space-between" align="center" mt={1}>
                            <Text fontSize="xs" color={COLORS.inkSoft}>Exp: {member.experience}</Text>
                            <Text fontSize="11px" color={COLORS.inkSoft} isTruncated>Mgr: {member.reportingManager}</Text>
                          </Flex>
                        </Box>
                      </Flex>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            </Flex>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
